// @flow
import * as React from 'react';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import ApiForm from 'esm-web-pro/containers/forms/ApiForm';
import { Box, Text } from 'grommet';
import { FormattedMessage } from 'react-intl';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';

type ReturnType = [
    Object[], { current: null | React.Node }, null | React.Node
];
/*
    A hook that allows a fast creation of a specific record from a dropdown field.
    Properties:
        fieldName - the field from the form where you want to create a new record;
        valueKey - backend accessor;
        options: {
            panelId - key for the side panel for creating the record;
            excludeFields - array with the names of the fields to be excluded from the new form;
            resourceName - the resource to be used when creating the new record;
            resourceArguments - any resource arguments needed (usually for nested resources);
            multi - creating multiple records at the same time;
            title - Side panel title (React Node);
        }
*/
const useCreatableField = (
    fieldName: string, valueKey: string = 'uuid', options = {}, formRef?: { current: Object | null },
): ReturnType => {
    const [createdEntity, setCreatedEntity] = React.useState(null);
    const [showCreator, setShowCreator] = React.useState(null);
    const formikRef = React.useRef();

    const PANEL_ID = options.panelId || 'panel-default-key';
    const EXCLUDE_FIELDS = options.excludeFields || [];

    const handleCreateOption = React.useCallback((inputValue: string) => {
        setShowCreator(inputValue);
    }, []);

    const handleFormSave = React.useCallback((response) => {
        setCreatedEntity(response);
        const ref = formRef || formikRef;
        if (ref.current) {
            const formikBag = ref.current.getFormikBag();
            if (options.multi) {
                const currentValue = formikBag.values[fieldName] || [];
                formikBag.setFieldValue(fieldName, [
                    ...currentValue,
                    `${response[valueKey]}`,
                ]);
            } else {
                formikBag.setFieldValue(fieldName, `${response[valueKey]}`);
            }
            formikBag.setFieldTouched(fieldName, true);
        }
        setShowCreator(null);
    }, [fieldName, valueKey, formRef]);

    const handleClose = React.useCallback(() => {
        setShowCreator(null);
    }, []);

    const formatOptionLabel = React.useCallback((data, meta) => {
        if (meta.context === 'value') {
            return data.label;
        }
        if (data.__isNew__) {
            return (
                <Text weight="bold">
                    {data.label}
                </Text>
            );
        }
        let createdOn = (
            <FormattedMessage id="now" defaultMessage="Now" />
        );
        if (data._original && data._original.created) {
            const createdOnDate = parseISO(data._original.created);
            createdOn = format(createdOnDate, 'dd.MM.yyyy HH:mm');
        }
        // TODO: Labels/Tags
        return (
            <>
                {data.label}
                <br />
                <Text size="small">
                    <FormattedMessage id="created-entity-dropdown" defaultMessage="Created" />
                    {': '}
                    {createdOn}
                </Text>
            </>
        );
    }, []);

    const extraSchema = React.useMemo(() => {
        const choices = {};

        if (createdEntity) {
            choices[createdEntity[valueKey]] = createdEntity.name;
        }

        return [{
            [`${fieldName}.ui.choices`]: choices,
            [`${fieldName}.ui.creatable`]: true,
            [`${fieldName}.ui.onCreateOption`]: handleCreateOption,
            [`${fieldName}.ui.formatOptionLabel`]: formatOptionLabel,
            [`${fieldName}.ui.additional_filters`]: {
                ordering: '-created',
            },
        }];
    }, [fieldName, valueKey, createdEntity, handleCreateOption, formatOptionLabel]);

    const creatorInitial = React.useMemo(() => ({
        name: showCreator,
    }), [showCreator]);

    const creator = showCreator ? (
        <SidePane
            openInitially
            id={PANEL_ID}
            title={options.title}
        >
            <Box justify="between" height="100%">
                <ApiForm
                    excludeFields={EXCLUDE_FIELDS}
                    resourceName={options.resourceName}
                    resourceArguments={options.resourceArguments || []}
                    onSuccess={handleFormSave}
                    height="100%"
                    initial={creatorInitial}
                    onClose={handleClose}
                />
            </Box>
        </SidePane>
    ) : null;

    return [extraSchema, formikRef, creator];
};

export default useCreatableField;
